import { useState, useEffect, useRef } from 'react';
import { PanelLeft, Search, Plus, LogOut, Menu, Inbox } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiGet, apiPost } from '../lib/api';
import { InboxTray } from './InboxTray';

interface HeaderProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onSearchClick?: () => void;
  showCapture?: boolean;
  onToggleCapture?: () => void;
}

export function Header({ sidebarOpen, onToggleSidebar, onSearchClick, showCapture: externalShowCapture, onToggleCapture }: HeaderProps) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [internalShowCapture, setInternalShowCapture] = useState(false);
  const showCapture = externalShowCapture ?? internalShowCapture;
  const toggleCapture = onToggleCapture ?? (() => setInternalShowCapture(prev => !prev));
  const closeCapture = onToggleCapture ? () => onToggleCapture() : () => setInternalShowCapture(false);
  const [captureText, setCaptureText] = useState('');
  const [inboxCount, setInboxCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showInboxTray, setShowInboxTray] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inboxTrayRef = useRef<HTMLDivElement>(null);

  // Load inbox count on mount and periodically
  useEffect(() => {
    loadInboxCount();
    const interval = setInterval(loadInboxCount, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Focus input when popover opens
  useEffect(() => {
    if (showCapture && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCapture]);

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        closeCapture();
      }
    }
    if (showCapture) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCapture]);

  // Close inbox tray on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (inboxTrayRef.current && !inboxTrayRef.current.contains(e.target as Node)) {
        setShowInboxTray(false);
      }
    }
    if (showInboxTray) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showInboxTray]);

  async function loadInboxCount() {
    try {
      const data = await apiGet<{ count: number }>('/quick-captures/count');
      setInboxCount(data.count);
    } catch {
      // silently ignore — badge just won't update
    }
  }

  async function handleSubmit() {
    const trimmed = captureText.trim();
    if (!trimmed || saving) return;

    setSaving(true);
    try {
      await apiPost('/quick-captures', { text: trimmed });
      setCaptureText('');
      closeCapture();
      toast.success('Captured!');
      loadInboxCount();
      window.dispatchEvent(new Event('quick-capture-added'));
    } catch (err) {
      console.error('Failed to save capture:', err);
      toast.error('Failed to save capture');
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      closeCapture();
    }
  }

  return (
    <header className="flex items-center justify-between h-[52px] px-4 border-b border-border bg-bg-secondary">
      <div className="flex items-center gap-2">
        {/* Mobile hamburger — always visible on mobile */}
        <button
          onClick={onToggleSidebar}
          className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors sm:hidden flex-shrink-0"
          title="Toggle sidebar"
        >
          <Menu size={18} />
        </button>
        {/* Desktop sidebar open button — slides in from left when sidebar is collapsed */}
        <div
          className="overflow-hidden transition-all duration-600 ease-in-out flex-shrink-0 hidden sm:block"
          style={{
            width: sidebarOpen ? 0 : 30,
            opacity: sidebarOpen ? 0 : 1,
          }}
        >
          <button
            onClick={onToggleSidebar}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
            title="Open sidebar"
          >
            <PanelLeft size={18} />
          </button>
        </div>
        <button
          onClick={onSearchClick}
          className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md bg-bg-input text-text-muted hover:text-text-secondary text-sm transition-colors"
        >
          <Search size={14} />
          <span className="hidden sm:inline">Search...</span>
          <kbd className="hidden sm:inline ml-4 text-xs text-text-muted bg-bg-surface px-1.5 py-0.5 rounded">
            Ctrl+K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative" ref={inboxTrayRef}>
          <button
            onClick={() => { setShowInboxTray(prev => !prev); if (showCapture) closeCapture(); }}
            className={`p-1.5 rounded-md transition-colors relative ${
              showInboxTray
                ? 'text-accent bg-bg-hover'
                : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }`}
            title="Inbox"
          >
            <Inbox size={18} />
            {inboxCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-danger text-white text-[10px] font-bold px-1">
                {inboxCount > 99 ? '99+' : inboxCount}
              </span>
            )}
          </button>
          {showInboxTray && (
            <InboxTray
              onClose={() => setShowInboxTray(false)}
              onCountChange={loadInboxCount}
            />
          )}
        </div>
        <div className="relative" ref={popoverRef}>
          <button
            onClick={() => { toggleCapture(); setShowInboxTray(false); }}
            className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Quick Capture</span>
          </button>

          {showCapture && (
            <div className="fixed left-4 right-4 top-[60px] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 bg-bg-surface border border-border rounded-lg shadow-xl z-50 p-3">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={captureText}
                  onChange={(e) => setCaptureText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Capture a thought..."
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm disabled:opacity-50"
                />
                <button
                  onClick={handleSubmit}
                  disabled={!captureText.trim() || saving}
                  className="px-3 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
                >
                  Save
                </button>
              </div>
              <p className="text-[11px] text-text-muted mt-2">
                Press Enter to save, Esc to close
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
          {user?.photoURL && (
            <img
              src={user.photoURL}
              alt=""
              className="w-7 h-7 rounded-full"
            />
          )}
          <span className="text-sm text-text-secondary hidden sm:inline">
            {user?.displayName || user?.email}
          </span>
          <button
            onClick={logout}
            className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}
